import * as React from 'react';
import { em, percent, px, url, viewWidth } from 'csx';
import { colorBrand } from '../theme/color';
import { center, horizontal, horizontallySpaced, vertical, verticallySpaced } from 'csstips';
import { style } from 'typestyle';
import { mediaTablet, mediaMobileOnly } from '../theme/media';
import { observer } from 'mobx-react';
import { ButtonPill } from './button-pill';
import { ModalGeneric } from './modal-generic';
import {UICreator} from '../types/ui';
import {ResponsiveImage} from './responsive-image';
import {ImagePreloader} from '../types/fn';

export interface PageHomeCreatorsModel {
  creators: Array<UICreator>;
}

export interface PageHomeCreatorsProps {
  model: PageHomeCreatorsModel;
  preload: ImagePreloader;
  i18n?: {
    title: string;
    view_cta: string;
  }
}

export interface PageHomeCreatorsState {
  showModal: boolean;
  creator: UICreator;
}

@observer
export class PageHomeCreators extends React.Component<PageHomeCreatorsProps, PageHomeCreatorsState> {
  state = {
    showModal: false,
    creator: null
  };

  render() {
    const { model, i18n = PageHomeCreators.i18n, preload } = this.props;
    const { showModal, creator } = this.state;

    return (
      <div className={PageHomeCreators.styles.container}>
        <h3 className={PageHomeCreators.styles.heading}>{i18n.title}</h3>
        <div className={PageHomeCreators.styles.creators}>
          {model.creators.map((creator, index) => (
            <div className={PageHomeCreators.styles.creator} key={index.toString()}>
              <ResponsiveImage
                className={PageHomeCreators.styles.creatorImage}
                asBackground={true}
                preload={preload}
                identity={creator.image}
              />
              <div className={PageHomeCreators.styles.creatorMeta}>
                <div>
                  <a className={PageHomeCreators.styles.creatorLink} href={`https://www.youtube.com/user/${creator.channel_id}`} target="_blank">
                    {creator.channel_name}
                  </a>
                </div>
                <div>
                  <ButtonPill onClick={this.showVideoModal.bind(this, creator)}>{i18n.view_cta}</ButtonPill>
                </div>
              </div>
            </div>
          ))}
        </div>
        <ModalGeneric model={{ show: showModal }} onRequestClose={this.hideVideoModal}>
          {creator && <h3>{creator.video_title}</h3>}
          {creator && (
            <div className={PageHomeCreators.styles.video}>
              <div dangerouslySetInnerHTML={{ __html: decodeURIComponent(creator.video_embed) }} />
            </div>
          )}
        </ModalGeneric>
      </div>
    );
  }

  showVideoModal = (creator: UICreator, event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.setState({
      showModal: true,
      creator
    });
  };

  hideVideoModal = () => {
    this.setState({
      showModal: false,
      creator: null
    });
  };

  static i18n = {
    title: 'Meet the creators using Lickd',
    view_cta: 'Watch video'
  };

  static styles = {
    container: style(
      mediaMobileOnly({
        padding: '80px 20px 40px',
      }),
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(30),
        padding: '100px 100px 60px',
      })
    ),
    heading: style({
      margin: '0 0 30px 0',
      color: colorBrand.toString(),
      fontSize: px(36)
    }),
    creators: style(
      {
        width: percent(100)
      },
      mediaMobileOnly({
        ...center,
        ...vertical,
        ...verticallySpaced(30),
      }),
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(30),
      })
    ),
    creator: style(
      {
        ...vertical,
        ...verticallySpaced(30),
      },
      mediaMobileOnly({
        lineHeight: em(1.2),
        padding: '0 20px',
      }),
      mediaTablet({
        flex: 1,
      }),
    ),
    creatorImage: style(
      {
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      },
      mediaMobileOnly({
        width: viewWidth(60),
        height: viewWidth(60),
        borderRadius: viewWidth(60),

      }),
      mediaTablet({
        width: percent(100),
        height: 'auto',
        paddingTop: percent(100),
        borderRadius: viewWidth(17),
      })
    ),
    creatorMeta: style({
      ...vertical,
      ...verticallySpaced(10),
      ...center
    }),
    creatorLink: style({
      fontWeight: 'bold'
    }),
    video: style({
      width: percent(100),
      marginTop: px(20),
      $nest: {
        '& iframe': {
          maxWidth: percent(100)
        }
      }
    }),
  }
}